import { useState } from 'react';
import { useCallback } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { MultiDetailSection } from 'src/modals/common';
import { DuplicateListingSection } from 'src/modals/ListingDetails/components/DuplicateListingSection/DuplicateListingSection';
import { EnableNativeSyncSection } from 'src/modals/ListingDetails/components/EnableNativeSyncSection/EnableNativeSyncSection';
import { ProofOfPurchaseSection } from 'src/modals/ListingDetails/components/ProofOfPurchaseSection/ProofOfPurchaseSection';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ListingDetails,
  ListingStatus,
  Marketplace,
} from 'src/WebApiController';
import { Feature, ListingClient } from 'src/WebApiController';

import { useClientSideMarketplaceListingStatus } from './MarketplaceBroadcastSection.hooks';
import { MarketplaceTable } from './MarketplaceTable';

export const MarketplaceBroadcastSection = ({
  listing,
  disabled,
  setLoading,
}: {
  listing: ListingDetails;
  disabled?: boolean;
  setLoading: (nextValue: boolean) => void;
}) => {
  const [ignoreDup, setIgnoreDup] = useState(listing?.ignoreDup ?? false);

  const { showErrorDialog } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();
  const hasIgnoreDupFeature = useUserHasFeature(Feature.IgnoreDuplicate);

  const [marketplaceListingStatus, setMarketplaceListingStatus] =
    useClientSideMarketplaceListingStatus(listing);

  const handleIgnoreDuplicateClick = useCallback(
    async (isChecked: boolean) => {
      setLoading(true);
      setIgnoreDup(isChecked);
      tryInvokeApi(
        async () => {
          const client = new ListingClient(activeAccountWebClientConfig);
          const success = await client.updateListingIgnoreDuplicate(
            listing?.id,
            isChecked
          );

          if (!success) {
            setIgnoreDup(!isChecked);
          } else if (listing) {
            refreshExpandedListItems();

            const unbroadcastResult = await client.deleteMarketplaceListings(
              listing.id,
              []
            );
            if (unbroadcastResult) {
              const newStatuses = Object.keys(marketplaceListingStatus).reduce(
                (acc, marketplace) => {
                  acc[marketplace as Marketplace] = ListingStatus.Delisted;
                  return acc;
                },
                {} as Record<Marketplace, ListingStatus>
              );
              setMarketplaceListingStatus(newStatuses);
            }
          }
        },
        (error) => {
          showErrorDialog('ListingClient.updateListingIgnoreDuplicate', error, {
            trackErrorData: { ListingId: listing?.id },
          });
        },
        () => {
          setLoading(false);
        }
      );
    },
    [
      activeAccountWebClientConfig,
      listing,
      marketplaceListingStatus,
      refreshExpandedListItems,
      setLoading,
      setMarketplaceListingStatus,
      showErrorDialog,
    ]
  );

  const isDuplicate =
    !!listing?.dupListingId && (!hasIgnoreDupFeature || !ignoreDup);

  const isDisabled = disabled || isDuplicate;

  const sections = [
    <EnableNativeSyncSection key="1" listing={listing} disabled={disabled} />,
    <ProofOfPurchaseSection key="2" listing={listing} />,
    <DuplicateListingSection
      key="3"
      listing={listing}
      allowBroadcast={ignoreDup ?? false}
      handleClick={handleIgnoreDuplicateClick}
    />,
  ];

  return (
    <>
      <MultiDetailSection
        name={<Content id={ContentId.Broadcast} />}
        extraContent={sections}
      >
        <MarketplaceTable
          disabled={isDisabled}
          listing={listing}
          marketplaceListingStatus={marketplaceListingStatus}
          setMarketplaceListingStatus={setMarketplaceListingStatus}
        />
      </MultiDetailSection>
    </>
  );
};
